import {
  AnalyticsProgramTitle,
  CatchupMode,
  CategoryIdHumanized,
  NO_EPG_DATA,
} from '@/constants'
import { type IEpgProgramInfoOVP } from '@setplex/tria-api'
import { CategoryId } from '~/entities/epg/categories'
import { ProgramStatus } from './index.h'

export function getHumanizedCategoryId(
  id: CategoryId | number
): CategoryIdHumanized | string {
  switch (id) {
    case CategoryId.All:
      return CategoryIdHumanized.All
    case CategoryId.Favorite:
      return CategoryIdHumanized.Favorite
    default:
      return String(id)
  }
}

/**
 * Determines the status of a program based on the `isLive` and `isPast` fields.
 *
 * @param {Object} program - The program object.
 * @param {boolean} program.isLive - Flag indicating whether the program is currently live.
 * @param {boolean} program.isPast - Flag indicating whether the program has already ended.
 * @param {boolean} program.isCatchup - Flag indicating whether the program is a catchup program.
 * @returns {ProgramStatus} - The status of the program: `ProgramStatus.Past`, `ProgramStatus.Live`, or `ProgramStatus.Future`.
 */
export type AnalyticsProgramInfoInput = {
  programTitle?: string
  isCatchup?: boolean
  catchUp?: {
    mode?: CatchupMode | 'HOURLY' | 'EPG'
  } | null
  isLive: boolean
  isPast: boolean
}

export const getProgramStatus = (
  program: AnalyticsProgramInfoInput
): ProgramStatus => {
  switch (true) {
    case program.isLive:
      return ProgramStatus.Live
    case program.isCatchup: // isCatchup should be upper than isPast
      return ProgramStatus.CatchUp
    case program.isPast:
      return ProgramStatus.Past
    default:
      return ProgramStatus.Future
  }
}

// At present time 'no epg' program title for Analytics is different from display title: 'No Program Data' -> 'No EPG data'
export const getAnalyticsProgramInfo = (
  program: AnalyticsProgramInfoInput
): Pick<IEpgProgramInfoOVP, 'programTitle' | 'programStatus'> => {
  const { programTitle, isCatchup, catchUp } = program
  let title = ''

  switch (true) {
    case isCatchup && catchUp?.mode === CatchupMode.Hourly:
      title = AnalyticsProgramTitle.HOURLY_CATCHUP_EPG
      break
    default:
      title =
        // as NO_EPG_DATA programs already has programTitle === NO_EPD_DATA we have to replace it with AnalyticsProgramTitle.NO_EPG_DATA
        programTitle === NO_EPG_DATA || !programTitle
          ? AnalyticsProgramTitle.NO_EPG_DATA
          : programTitle
      break
  }

  return {
    programTitle: title,
    programStatus: getProgramStatus(program),
  }
}
